import { customElement, property, state } from 'lit/decorators.js';
import { CSSResult, html, LitElement, nothing, TemplateResult, unsafeCSS } from 'lit';
import { consume } from '@lit/context';
import productView from '@breuni-ui/design-tokens/images/icons/o-ic-24_shirt.svg?element';
import modelView from '@breuni-ui/design-tokens/images/icons/o-ic-24_model-view.svg?element';
import vtoIcon from '@breuni-ui/design-tokens/images/icons/o-ic-24_virtual-tryon.svg?element';
import styles from './view-switch.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { Messages, SwitchModelContainer, ViewMode } from '../../../@types/wishlist.js';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import { wishlistAppConfigContext, WishlistAppConfig } from '../../../scripts/context/appConfigContext.js';
import { vtoContext } from '../../../scripts/context/vtoContext.js';
import { VtoService } from '../../../scripts/service/vto-service.js';
import { featureSeen } from '../../../scripts/service/feature-seen-service.js';
import '../feature-indicator/feature-indicator.js';

const VTO_MODELSWITCH_FEATURE_KEY = 'vto-modelswitch';

@customElement('ents-view-switch')
export class ViewSwitch extends LitElement {
  @property({ type: String, attribute: 'current-view' })
  private currentView: ViewMode = 'product';

  @property({ type: Boolean, attribute: 'logged-in' })
  private loggedIn: boolean = false;

  @property({ type: String })
  private layout: 'icons' | 'cycle' = 'icons';

  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: vtoContext })
  private vtoService?: VtoService;

  @state()
  private showDot: boolean = false;

  private switchingView = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.showDot =
      this.vtoAvailable &&
      this.wishlistAppConfig?.showVtoModelswitchIndicator &&
      !featureSeen.isSeen(VTO_MODELSWITCH_FEATURE_KEY);
  }

  private get vtoAvailable(): boolean {
    return this.layout === 'icons' && this.wishlistAppConfig?.vtoEnabled && !!this.vtoService?.isAvailable();
  }

  private get views(): ViewMode[] {
    return this.vtoAvailable ? ['product', 'model', 'vto'] : ['product', 'model'];
  }

  protected render(): TemplateResult {
    return this.layout === 'cycle' ? this.renderCycle() : this.renderIcons();
  }

  private renderIcons(): TemplateResult {
    return html`
      <div class="ents-view-switch" role="group" data-test-ents-view-switch>
        ${this.renderIconButton('product', productView())} ${this.renderIconButton('model', modelView())}
        ${this.vtoAvailable ? this.renderIconButton('vto', vtoIcon(), true) : nothing}
      </div>
    `;
  }

  private renderIconButton(view: ViewMode, icon: SVGElement, withDot = false) {
    const iconSpan = html`
      <span class="ents-view-switch__icon ${this.currentView === view ? 'ents-view-switch__icon--active' : ''}">
        ${hideIconFromAssistiveTechnology(icon)}
      </span>
    `;
    return html`
      <button
        class="ents-view-switch__button"
        type="button"
        title="${this.messages[`ents_wishlist_images_switch_${view}`]}"
        aria-pressed="${this.currentView === view}"
        @click="${() => this.handleSelect(view)}"
        data-test-ents-view-switch-button="${view}"
      >
        ${
          withDot
            ? html`<ents-feature-indicator position="after-top" ?visible="${this.showDot}"
                >${iconSpan}</ents-feature-indicator
              >`
            : iconSpan
        }
      </button>
    `;
  }

  private renderCycle(): TemplateResult {
    return html`
      <button
        class="ents-filter-overview__button ents-filter-overview__button__iconbox ents-copy"
        data-test-ents-filter-overview-button="modelViewSwitch"
        title="${this.messages[`ents_wishlist_images_switch_${this.getNextView()}`]}"
        @click="${this.handleCycle}"
        data-test-ents-wishlist-toggle-button-model-view
      >
        ${this.messages['ents_wishlist_filter_group_view']}
        <div class="ents-filter-overview-icon-container">
          <span
            class="ents-filter-overview__button__icon ${
              this.currentView === 'product' ? 'ents-filter-overview__button__icon--active' : ''
            }"
            data-test-ents-wishlist-product-view-icon
          >
            ${hideIconFromAssistiveTechnology(productView())}
          </span>
          <span
            class="ents-filter-overview__button__icon ${
              this.currentView === 'model' ? 'ents-filter-overview__button__icon--active' : ''
            }"
            data-test-ents-wishlist-model-view-icon
          >
            ${hideIconFromAssistiveTechnology(modelView())}
          </span>
        </div>
      </button>
    `;
  }

  private getNextView(): ViewMode {
    const views = this.views;
    return views[(views.indexOf(this.currentView) + 1) % views.length];
  }

  private handleCycle = (): void => {
    void this.handleSelect(this.getNextView());
  };

  private handleSelect = async (view: ViewMode): Promise<void> => {
    if (this.switchingView) return;
    this.switchingView = true;
    try {
      if (view === 'vto') {
        this.markDotSeen();
        if (!this.vtoService) return;
        let profile = await this.vtoService.getProfileImage();
        if (!profile) {
          // requestProfileImage may resolve null even when a profile exists; getProfileImage is authoritative.
          profile = (await this.vtoService.requestProfileImage()) ?? (await this.vtoService.getProfileImage());
          if (!profile) return;
        }
      }
      this.switchTo(view);
    } finally {
      this.switchingView = false;
    }
  };

  private markDotSeen(): void {
    if (!this.showDot) return;
    this.showDot = false;
    featureSeen.markSeen(VTO_MODELSWITCH_FEATURE_KEY, {
      loggedIn: this.loggedIn,
      endpoint: this.wishlistAppConfig.featureApiEndpoint,
    });
  }

  private switchTo(view: ViewMode): void {
    this.currentView = view;
    this.dispatchEvent(
      new CustomEvent<SwitchModelContainer>('ents:switch-model-view', {
        detail: { view },
        bubbles: true,
        composed: true,
      }),
    );
  }
}
